import { useMemo, useState } from "react";
import { Finance } from "../lib/api";
import { toast } from "sonner";
import { X, User, Calculator, CheckCircle2 } from "lucide-react";

/**
 * Salary entry form with an integrated ZUS calculator (Polish payroll).
 *
 * Business rule (per user request):
 *   • No VAT applies to wages.
 *   • KUP (deductible cost) is split into 2 rows: wynagrodzenie brutto AND
 *     ZUS pracodawcy — this matches Polish bookkeeping practice (konto 404 vs
 *     konto 405). Both are inserted as separate transactions in the KUP.
 *
 * Approximate 2026 rates used (employer + employee, standard classifications):
 *   Emerytalna: 9.76% + 9.76%
 *   Rentowa:    1.5%  + 6.5%
 *   Wypadkowa:            1.67%  (only employer, industry-average)
 *   Chorobowa:  2.45%              (mandatory for o pracę, optional zlecenie)
 *   FP:                   2.45%  (only employer)
 *   FGŚP:                 0.10%  (only employer)
 *   Zdrowotna:  9%                (employee, base = brutto - ZUS społ.)
 *   PIT zaliczka: 12% flat rate above koszty uzyskania (250 zł) minus miesięczna
 *     ulga podatkowa (300 zł) — 1/12 of annual 3600 zł free amount.
 *
 * Wynagrodzenie autora / umowa o dzieło i powołanie: bez ZUS, tylko PIT.
 * B2B: pominięte (traktowane jako faktura, nie wynagrodzenie).
 */

const RATES = {
  emerytalna: 0.0976,
  rentowa_emp: 0.015,
  rentowa_pr: 0.065,
  chorobowa: 0.0245,
  wypadkowa: 0.0167,
  fp: 0.0245,
  fgsp: 0.001,
  zdrowotna: 0.09,
  pit: 0.12,
  pit_koszty: 250,
  pit_ulga: 300,
};

const CONTRACT_TYPES = [
  { id: "o_prace", label: "Umowa o pracę", chorobowaLocked: true },
  { id: "zlecenie", label: "Umowa zlecenie", chorobowaLocked: false },
  { id: "dzielo", label: "Umowa o dzieło" },
  { id: "powolanie", label: "Powołanie" },
];

function num(v) {
  const n = parseFloat(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}
const round2 = (n) => Math.round(n * 100) / 100;

/**
 * Compute the ZUS + PIT breakdown for a given gross wage and contract type.
 * Returns null when brutto <= 0.
 */
function computeBreakdown(brutto, contractType, options = {}) {
  const b = num(brutto);
  if (b <= 0) return null;

  // Uczeń/Student na umowie zlecenie do 26 r.ż. — zwolnienie z ZUS
  // (społecznych + zdrowotnej). Dotyczy tylko umowy zlecenia.
  const isStudentZlecenie = contractType === "zlecenie" && options.student;

  let zusEmp = 0; // employee social security (deducted from brutto)
  let zusPr = 0;  // employer social security (added ON TOP of brutto)
  if (contractType === "o_prace") {
    zusEmp = b * (RATES.emerytalna + RATES.rentowa_emp + RATES.chorobowa);
    zusPr = b * (RATES.emerytalna + RATES.rentowa_pr + RATES.wypadkowa + RATES.fp + RATES.fgsp);
  } else if (contractType === "zlecenie" && !isStudentZlecenie) {
    zusEmp = b * (RATES.emerytalna + RATES.rentowa_emp + (options.chorobowa ? RATES.chorobowa : 0));
    zusPr = b * (RATES.emerytalna + RATES.rentowa_pr + RATES.wypadkowa + RATES.fp + RATES.fgsp);
  }
  // dzielo / powolanie / student-zlecenie → no ZUS (both sides = 0)

  const zdrowotnaBase = Math.max(0, b - zusEmp);
  let zdrowotna;
  if (contractType === "dzielo" || isStudentZlecenie) {
    zdrowotna = 0;
  } else if (contractType === "powolanie") {
    // Powołanie (członek zarządu / prokurent): 9% składki zdrowotnej liczonej
    // od pełnego brutta (brak ZUS społ. do odjęcia).
    zdrowotna = round2(b * RATES.zdrowotna);
  } else {
    zdrowotna = round2(zdrowotnaBase * RATES.zdrowotna);
  }

  // PIT base for employment types = brutto - zusEmp - koszty uzyskania
  let pitBase;
  let pit = 0;
  if (contractType === "dzielo") {
    pitBase = Math.max(0, b - b * 0.2);
    pit = pitBase * RATES.pit;
  } else if (contractType === "powolanie") {
    pitBase = Math.max(0, b - RATES.pit_koszty);
    pit = pitBase * RATES.pit;
  } else {
    pitBase = Math.max(0, b - zusEmp - RATES.pit_koszty);
    pit = pitBase * RATES.pit;
    if (contractType === "o_prace" || options.pit2) pit = Math.max(0, pit - RATES.pit_ulga);
  }
  // Under 26 OR student → PIT = 0 for umowa o pracę / zlecenie
  if ((options.under26 || isStudentZlecenie) && (contractType === "zlecenie" || contractType === "o_prace")) {
    pit = 0;
  }

  const netto = round2(b - zusEmp - zdrowotna - pit);
  const totalCost = round2(b + zusPr);
  return {
    brutto: round2(b),
    zusEmp: round2(zusEmp),
    zusPr: round2(zusPr),
    zdrowotna: round2(zdrowotna),
    pit: round2(pit),
    netto,
    totalCost,
    isStudentZlecenie,
  };
}

/**
 * Reverse the ZUS calculator: given desired NET (na rękę) and contract type,
 * find the BRUTTO that produces exactly that netto. Uses bisection because the
 * forward function is piecewise linear (ulga 300 zł introduces a step).
 * Converges in ~40 iterations to ±1 grosz precision.
 */
function reverseBrutto(targetNetto, contractType, options = {}) {
  const target = num(targetNetto);
  if (target <= 0) return 0;
  let lo = 0;
  let hi = target * 3; // safe upper bound
  for (let i = 0; i < 60; i++) {
    const mid = (lo + hi) / 2;
    const bd = computeBreakdown(mid, contractType, options);
    if (!bd) return 0;
    if (Math.abs(bd.netto - target) < 0.005) return mid;
    if (bd.netto < target) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
}

const monthNow = new Date().toISOString().slice(0, 7);

export default function SalaryForm({ employees, onClose, onSaved }) {
  const [form, setForm] = useState({
    employee_id: "",
    contract_type: "o_prace",
    period: monthNow, // za jaki miesiąc rozliczenia
    payment_date: new Date().toISOString().slice(0, 10),
    brutto: "",
    netto: "", // do wypłaty (na rękę) — bidirectional with brutto
    chorobowa: true, // for zlecenie only
    under26: false,
    student: false, // uczeń/student — dla zlecenia zwalnia z ZUS + zdrowotnej
    pit2: true, // PIT-2 declaration (300 ulga)
  });
  const [saving, setSaving] = useState(false);

  // Options object for calculators — order matches computeBreakdown() signature
  const calcOptions = useMemo(
    () => ({
      chorobowa: form.chorobowa,
      under26: form.under26,
      student: form.student,
      pit2: form.pit2,
    }),
    [form.chorobowa, form.under26, form.student, form.pit2]
  );

  const breakdown = useMemo(
    () => computeBreakdown(form.brutto, form.contract_type, calcOptions),
    [form.brutto, form.contract_type, calcOptions]
  );

  // Handlers that keep brutto/netto in sync — both are user-editable, we pick
  // whichever was typed most recently as the source of truth.
  const onBruttoChange = (v) => {
    const bd = computeBreakdown(v, form.contract_type, calcOptions);
    setForm((f) => ({
      ...f,
      brutto: v,
      netto: bd ? bd.netto.toFixed(2) : "",
    }));
  };
  const onNettoChange = (v) => {
    const target = num(v);
    if (target <= 0) {
      setForm((f) => ({ ...f, netto: v, brutto: "" }));
      return;
    }
    const b = reverseBrutto(target, form.contract_type, calcOptions);
    setForm((f) => ({ ...f, netto: v, brutto: b > 0 ? round2(b).toFixed(2) : "" }));
  };

  // If contract type or options change, recompute netto from current brutto
  // (or brutto from current netto) to keep the pair consistent.
  const recomputePair = (patch) => {
    setForm((prev) => {
      const next = { ...prev, ...patch };
      // Prefer using brutto as source when both are set
      if (next.brutto) {
        const bd = computeBreakdown(next.brutto, next.contract_type, {
          chorobowa: next.chorobowa,
          under26: next.under26,
          student: next.student,
          pit2: next.pit2,
        });
        next.netto = bd ? bd.netto.toFixed(2) : "";
      } else if (next.netto) {
        const b = reverseBrutto(num(next.netto), next.contract_type, {
          chorobowa: next.chorobowa,
          under26: next.under26,
          student: next.student,
          pit2: next.pit2,
        });
        next.brutto = b > 0 ? round2(b).toFixed(2) : "";
      }
      return next;
    });
  };

  const emp = employees.find((e) => e.id === form.employee_id);
  const contractMeta = CONTRACT_TYPES.find((c) => c.id === form.contract_type);
  const hasZus = form.contract_type === "o_prace" || form.contract_type === "zlecenie";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.employee_id) return toast.error("Wybierz pracownika");
    if (!breakdown) return toast.error("Podaj kwotę brutto");
    setSaving(true);
    try {
      const empName = emp ? `${emp.first_name} ${emp.last_name}` : "—";
      const ctLabel = contractMeta?.label || form.contract_type;
      const suffix = breakdown.isStudentZlecenie ? " [uczeń/student, bez ZUS]" : "";
      // 1. Wynagrodzenie brutto (KUP)
      await Finance.create({
        date: form.payment_date,
        due_date: form.payment_date,
        type: "expense",
        category: "Wynagrodzenia",
        amount: breakdown.brutto,
        net_amount: null,
        vat_rate: null,
        vat_amount: null,
        description: `Wynagrodzenie brutto — ${empName} — ${ctLabel}${suffix} — okres ${form.period} (netto do wypłaty: ${breakdown.netto.toFixed(2)} zł)`,
        status: "actual",
        employee_id: form.employee_id,
        vehicle_id: null,
      });
      // 2. ZUS pracodawcy (KUP) — only if applicable
      if (breakdown.zusPr > 0) {
        await Finance.create({
          date: form.payment_date,
          due_date: form.payment_date,
          type: "expense",
          category: "Wynagrodzenia",
          amount: breakdown.zusPr,
          net_amount: null,
          vat_rate: null,
          vat_amount: null,
          description: `ZUS pracodawcy — ${empName} — ${ctLabel} — okres ${form.period}`,
          status: "actual",
          employee_id: form.employee_id,
          vehicle_id: null,
        });
      }
      toast.success(`Zapisano wynagrodzenie (${breakdown.zusPr > 0 ? "2 wpisy" : "1 wpis"} w KUP)`);
      onSaved?.();
    } catch (err) {
      toast.error("Błąd zapisu: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9998] bg-black/50 flex items-center justify-center p-4"
      data-testid="salary-form-modal"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <form
        onSubmit={submit}
        className="bg-white w-full max-w-2xl max-h-[90vh] overflow-auto border-2 border-zinc-900 shadow-[8px_8px_0_0_#18181b]"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-900 bg-zinc-50">
          <div>
            <div className="font-label text-xs">// Nowe wynagrodzenie</div>
            <h2 className="font-heading font-black text-lg uppercase tracking-tight">
              Dodaj wynagrodzenie
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="salary-form-close"
            className="p-2 hover:bg-zinc-200"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <span className="font-label block mb-2">Pracownik</span>
              <select
                required
                data-testid="salary-emp-select"
                className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 focus:outline-none focus:border-zinc-900"
                value={form.employee_id}
                onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
              >
                <option value="">— wybierz —</option>
                {employees.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.first_name} {e.last_name} {e.position ? `· ${e.position}` : ""}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="font-label block mb-2">Rodzaj umowy</span>
              <select
                data-testid="salary-contract-type"
                className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 focus:outline-none focus:border-zinc-900"
                value={form.contract_type}
                onChange={(e) => recomputePair({ contract_type: e.target.value })}
              >
                {CONTRACT_TYPES.map((c) => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="font-label block mb-2">Okres rozliczeniowy</span>
              <input
                type="month"
                data-testid="salary-period"
                className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 font-mono focus:outline-none focus:border-zinc-900"
                value={form.period}
                onChange={(e) => setForm({ ...form, period: e.target.value })}
              />
            </label>
            <label className="block">
              <span className="font-label block mb-2">Data wypłaty</span>
              <input
                type="date"
                data-testid="salary-payment-date"
                className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 font-mono focus:outline-none focus:border-zinc-900"
                value={form.payment_date}
                onChange={(e) => setForm({ ...form, payment_date: e.target.value })}
              />
            </label>
          </div>

          <div className="border-2 border-zinc-900 bg-white p-4 space-y-3">
            <div className="font-label flex items-center justify-between">
              <span>Kwota — auto-kalkulacja</span>
              <span className="text-[10px] font-mono text-zinc-500">brutto ↔ netto</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-[10px] font-mono uppercase text-zinc-600 block mb-1">Brutto (zł)</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  data-testid="salary-brutto"
                  className="w-full border border-zinc-300 bg-white px-3 py-2 font-mono text-lg focus:outline-none focus:border-brand"
                  value={form.brutto}
                  onChange={(e) => onBruttoChange(e.target.value)}
                  placeholder="np. 5000.00"
                />
              </label>
              <label className="block">
                <span className="text-[10px] font-mono uppercase text-zinc-600 block mb-1">Do wypłaty — na rękę (zł)</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  data-testid="salary-netto-input"
                  className="w-full border border-zinc-300 bg-emerald-50 px-3 py-2 font-mono text-lg focus:outline-none focus:border-emerald-600"
                  value={form.netto}
                  onChange={(e) => onNettoChange(e.target.value)}
                  placeholder="np. 3738.45"
                />
              </label>
            </div>
            <div className="text-[11px] text-zinc-500 font-mono">
              💡 Wpisz w jednym polu — drugie policzy się samo. Zmiana rodzaju umowy lub opcji poniżej też zaktualizuje wyliczenia.
            </div>
          </div>

          {/* Options — contract-type dependent */}
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm border-t border-zinc-200 pt-4">
            {form.contract_type === "zlecenie" && (
              <>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.student}
                    onChange={(e) => recomputePair({ student: e.target.checked })}
                    data-testid="salary-student"
                    className="w-4 h-4 accent-emerald-600"
                  />
                  Status ucznia / studenta &lt; 26 lat (bez ZUS + PIT)
                </label>
                <label className={`inline-flex items-center gap-2 cursor-pointer ${form.student ? "opacity-40" : ""}`}>
                  <input
                    type="checkbox"
                    checked={form.chorobowa}
                    disabled={form.student}
                    onChange={(e) => recomputePair({ chorobowa: e.target.checked })}
                    data-testid="salary-chorobowa"
                    className="w-4 h-4 accent-zinc-900"
                  />
                  Ubezpieczenie chorobowe (dobrowolne)
                </label>
              </>
            )}
            {hasZus && (
              <>
                <label className={`inline-flex items-center gap-2 cursor-pointer ${form.student ? "opacity-40" : ""}`}>
                  <input
                    type="checkbox"
                    checked={form.under26}
                    disabled={form.student}
                    onChange={(e) => recomputePair({ under26: e.target.checked })}
                    data-testid="salary-under26"
                    className="w-4 h-4 accent-zinc-900"
                  />
                  Poniżej 26 lat (bez PIT)
                </label>
                {form.contract_type === "zlecenie" && (
                  <label className={`inline-flex items-center gap-2 cursor-pointer ${form.student ? "opacity-40" : ""}`}>
                    <input
                      type="checkbox"
                      checked={form.pit2}
                      disabled={form.student}
                      onChange={(e) => recomputePair({ pit2: e.target.checked })}
                      data-testid="salary-pit2"
                      className="w-4 h-4 accent-zinc-900"
                    />
                    PIT-2 (ulga 300 zł)
                  </label>
                )}
              </>
            )}
          </div>

          {/* Breakdown */}
          <div
            className="border border-zinc-900 bg-zinc-50 p-4 space-y-2"
            data-testid="salary-breakdown"
          >
            <div className="font-label flex items-center gap-2">
              <Calculator className="w-3 h-3" /> Rozliczenie ZUS + PIT
            </div>
            {!breakdown ? (
              <div className="text-sm text-zinc-500 italic">Wpisz kwotę brutto, aby zobaczyć rozliczenie</div>
            ) : (
              <>
                <BreakdownRow label="Brutto" value={breakdown.brutto} bold />
                <BreakdownRow
                  label={form.contract_type === "dzielo" || form.contract_type === "powolanie"
                    ? "ZUS pracownika (n/d)"
                    : "− ZUS pracownika (emerytalna+rentowa" + (form.chorobowa && hasZus ? "+chorobowa" : "") + ")"}
                  value={-breakdown.zusEmp}
                  muted={breakdown.zusEmp === 0}
                />
                <BreakdownRow
                  label="− Składka zdrowotna (9%)"
                  value={-breakdown.zdrowotna}
                  muted={breakdown.zdrowotna === 0}
                />
                <BreakdownRow
                  label="− Zaliczka PIT (12%)"
                  value={-breakdown.pit}
                  muted={breakdown.pit === 0}
                />
                <div className="border-t border-zinc-300 pt-2 mt-2">
                  <BreakdownRow label="NETTO do wypłaty" value={breakdown.netto} bold accent="emerald" data-testid="salary-netto" />
                </div>
                <div className="border-t border-zinc-300 pt-2 mt-2 space-y-1">
                  <BreakdownRow
                    label="+ ZUS pracodawcy (dodatkowo)"
                    value={breakdown.zusPr}
                    muted={breakdown.zusPr === 0}
                    accent="red"
                  />
                  <BreakdownRow
                    label="CAŁKOWITY KOSZT FIRMY (KUP)"
                    value={breakdown.totalCost}
                    bold
                    accent="red"
                    data-testid="salary-total-cost"
                  />
                </div>
                <div className="pt-3 mt-3 border-t border-dashed border-zinc-400 text-[11px] text-zinc-500 leading-relaxed">
                  📝 Zapiszemy jako {breakdown.zusPr > 0 ? "2 wpisy" : "1 wpis"} w KUP:
                  <br />
                  1. <b>Wynagrodzenie brutto</b> — {breakdown.brutto.toFixed(2)} zł
                  {breakdown.zusPr > 0 && (
                    <>
                      <br />
                      2. <b>ZUS pracodawcy</b> — {breakdown.zusPr.toFixed(2)} zł
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-zinc-200 bg-zinc-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-xs font-bold uppercase tracking-wider border border-zinc-300 hover:border-zinc-900"
          >
            Anuluj
          </button>
          <button
            type="submit"
            disabled={saving || !breakdown || !form.employee_id}
            data-testid="salary-submit"
            className="inline-flex items-center gap-2 bg-brand hover:bg-brand-hover disabled:opacity-50 text-white px-6 py-2 text-xs font-bold uppercase tracking-wider"
          >
            <CheckCircle2 className="w-4 h-4" />
            {saving ? "Zapisuję..." : "Zapisz wynagrodzenie"}
          </button>
        </div>
      </form>
    </div>
  );
}

function BreakdownRow({ label, value, bold, muted, accent, "data-testid": testId }) {
  const accentCls = accent === "red" ? "text-red-700" : accent === "emerald" ? "text-emerald-700" : "text-zinc-900";
  return (
    <div
      data-testid={testId}
      className={`flex items-center justify-between text-sm ${muted ? "opacity-40" : ""}`}
    >
      <span className={bold ? "font-bold" : ""}>{label}</span>
      <span className={`font-mono ${bold ? "font-bold text-base" : ""} ${accentCls}`}>
        {value.toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł
      </span>
    </div>
  );
}
